'use client';

import React from 'react';
import { SelectProps } from '@radix-ui/react-select';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { FormControl } from '@/components/ui/form';

/**
 * Represents an option for a simple select component.
 */
export interface SelectSimpleOption {
  value: string;
  label: string;
}

/**
 * Props for the SelectSimpleCds component.
 *
 * @extends SelectProps
 *
 * @property {SelectSimpleOption[]} options - An array of options to be displayed in the select dropdown.
 * @property {boolean} [hasFormControl] - Indicates if there is a FormControl component wrapping the SelectTrigger.
 * This prop is temporary and should be removed once a better way to determine this is implemented.
 */
export interface SelectSimpleCdsProps extends SelectProps {
  options: SelectSimpleOption[] | string[];
  // Is there a FormControl component wrapping the SelectTrigger?
  // TODO: Remove this prop once we have a better way to determine this.
  hasFormControl?: boolean;
}

/**
 * `SelectSimpleCds` is a React functional component that renders a custom select input with optional form control.
 * It uses `React.forwardRef` to pass a ref to the `SelectValue` component.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Array} props.options - An array of option objects to be displayed in the select dropdown.
 * @param {boolean} props.hasFormControl - A flag to determine if the select should be wrapped with form control.
 * @param {string} props.placeholder - A placeholder text for the select input.
 * @param {React.Ref<HTMLInputElement>} ref - A ref to be forwarded to the `SelectValue` component.
 *
 * @returns {JSX.Element} The rendered `SelectSimpleCds` component.
 */
const SelectSimpleCds = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'> & SelectSimpleCdsProps
>(({ options: optionsFromProps, hasFormControl, placeholder, ...props }, ref) => {
  const options = React.useMemo(
    () =>
      optionsFromProps.map((option) =>
        typeof option === 'string' ? { value: option, label: option } : option
      ),
    [optionsFromProps]
  );

  return (
    <Select {...props}>
      <FormControlConditionalWrapperCds hasFormControl={hasFormControl}>
        <SelectTrigger>
          <SelectValue placeholder={placeholder} ref={ref} />
        </SelectTrigger>
      </FormControlConditionalWrapperCds>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
});
SelectSimpleCds.displayName = 'SelectSimpleCds';

/**
 * A conditional wrapper component that wraps its children with a `FormControl` component
 * if the `hasFormControl` prop is true. Otherwise, it renders the children directly.
 *
 * @param {Object} props - The props object.
 * @param {React.ReactNode} props.children - The child elements to be wrapped.
 * @param {boolean} [props.hasFormControl] - A flag indicating whether to wrap the children with `FormControl`.
 * @returns {JSX.Element} The wrapped or unwrapped children based on the `hasFormControl` prop.
 */
function FormControlConditionalWrapperCds({
  children,
  hasFormControl,
}: {
  children: React.ReactNode;
  hasFormControl?: boolean;
}) {
  return hasFormControl ? <FormControl>{children}</FormControl> : <>{children}</>;
}

export { SelectSimpleCds, FormControlConditionalWrapperCds };
